import { useState, useCallback } from "react"
import { useHead } from "@unhead/react"
import {ArrowDown, Shield, Clock, BarChart3} from 'lucide-react'
import Navbar from "@/components/Navbar"
import CalculatorForm from "@/components/CalculatorForm"
import ResultsDisplay from "@/components/ResultsDisplay"
import AmortizationTable from "@/components/AmortizationTable"
import FeatureSection from "@/components/FeatureSection"
import FAQSection from "@/components/FAQSection"
import Footer from "@/components/Footer"
import { calculateLoan, type LoanInputs, type LoanResults } from "@/utils/calculator"

const HERO_IMAGE = "https://static.lumi.new/8b/8b800c505e419575d12613185bd93a8f.jpg"

const trustBadges = [
  { icon: Shield, text: "100% Free" },
  { icon: Clock, text: "Instant Results" },
  { icon: BarChart3, text: "Full Amortization" },
]

function Home() {
  const [results, setResults] = useState<LoanResults | null>(null)

  useHead({
    title: "Land Mortgage Calculator & Land Contract Payment Tool | Free Online Calculator",
    meta: [
      {
        name: "description",
        content:
          "Free land mortgage calculator and land contract payment tool. Calculate land loan payments, balloon payments, amortization schedules, and land financing options for raw land, hunting land, and land equity.",
      },
      {
        name: "keywords",
        content:
          "land financing calculator, land payment estimator, raw land mortgage calculator, hunting land mortgage calculator, land equity calculator, land note calculator, land contract balloon payment calculator, amortization schedule for land contracts",
      },
    ],
    htmlAttrs: { lang: "en" },
  })

  const handleCalculate = useCallback((inputs: LoanInputs) => {
    const result = calculateLoan(inputs)
    setResults(result)
    // Smooth scroll to results
    setTimeout(() => {
      document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden" id="calculator">
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Beautiful green farmland landscape"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/85 via-emerald-950/80 to-emerald-950/95" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
          {/* Hero text */}
          <div className="text-center mb-10 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-200 text-sm font-medium">
                Free Land Financing Calculator
              </span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-5 leading-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Land Mortgage Calculator
              <br />
              <span className="text-amber-300">&</span>{" "}
              <span className="text-emerald-300">Land Contract Payment Tool</span>
            </h1>

            <p className="text-emerald-100/90 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
              Calculate your land loan payments, balloon payment schedules, and complete amortization
              tables. The most trusted land payment estimator for raw land, hunting land, and land
              contract financing.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8">
              {trustBadges.map((badge) => (
                <div key={badge.text} className="flex items-center gap-2 text-emerald-200/80">
                  <badge.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </div>
              ))}
            </div>

            <a
              href="#calc-form"
              className="inline-flex items-center gap-2 text-emerald-300 hover:text-white transition-colors text-sm font-medium"
            >
              <ArrowDown className="w-4 h-4 animate-bounce" />
              Start Calculating Below
            </a>
          </div>

          {/* Calculator section */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start" id="calc-form">
            {/* Calculator Form */}
            <div className="animate-fade-in-up stagger-2">
              <CalculatorForm onCalculate={handleCalculate} />
            </div>

            {/* Results */}
            <div className="animate-fade-in-up stagger-3" id="results">
              {results ? (
                <ResultsDisplay results={results} />
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border-2 border-dashed border-white/20 p-8 sm:p-12 text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <BarChart3 className="w-8 h-8 text-emerald-300" />
                  </div>
                  <h3
                    className="text-xl font-bold text-white mb-2"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    Your Results Will Appear Here
                  </h3>
                  <p className="text-emerald-200/70 text-sm max-w-sm mx-auto">
                    Enter your land price, down payment, interest rate, and loan term, then click
                    "Calculate" to see your complete land loan breakdown.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Amortization Table */}
      {results ? (
        <section className="py-12 bg-gray-50" id="amortization">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <AmortizationTable results={results} />
          </div>
        </section>
      ) : null}

      {/* Feature Sections */}
      <FeatureSection />

      {/* FAQ */}
      <FAQSection />

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Home
